import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useUser } from '@/contexts/UserContext';
import { toast } from '@/hooks/use-toast';

interface InviteLink {
  id: string;
  invite_code: string;
  invite_url: string;
  invite_type: 'registration' | 'link_existing';
  expires_at: string;
  used_at: string | null;
  used_by_profile_id: string | null;
  created_at: string;
}

interface InvitedUser {
  id: string;
  display_name: string;
  email: string;
  created_at: string;
}

export const useVentureInvites = () => {
  const { currentUser } = useUser();
  const [invites, setInvites] = useState<InviteLink[]>([]);
  const [invitedUsers, setInvitedUsers] = useState<InvitedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const fetchInvites = async () => {
    if (!currentUser?.id) return;

    try {
      setLoading(true);

      // Fetch profile
      const { data: profile } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', currentUser.id)
        .single();

      if (!profile) {
        console.error('Profile not found');
        return;
      }

      // Fetch active invites
      const { data: invitesData, error: invitesError } = await supabase
        .from('invite_links')
        .select('*')
        .eq('venture_profile_id', profile.id)
        .order('created_at', { ascending: false });

      if (invitesError) {
        console.error('Error fetching invites:', invitesError);
        toast({
          title: 'Error',
          description: 'Failed to load invites',
          variant: 'destructive',
        });
        return;
      }

      setInvites((invitesData || []) as InviteLink[]);

      // Fetch invited users
      const { data: relationshipsData, error: relationshipsError } = await supabase
        .from('user_relationships')
        .select(`
          invitee_profile_id,
          created_at,
          profiles!user_relationships_invitee_profile_id_fkey (
            id,
            display_name,
            email
          )
        `)
        .eq('inviter_profile_id', profile.id)
        .order('created_at', { ascending: false });

      if (relationshipsError) {
        console.error('Error fetching relationships:', relationshipsError);
      } else {
        const users = relationshipsData?.map((rel: any) => ({
          id: rel.profiles.id,
          display_name: rel.profiles.display_name,
          email: rel.profiles.email,
          created_at: rel.created_at,
        })) || [];
        setInvitedUsers(users);
      }
    } catch (error) {
      console.error('Error in fetchInvites:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvites();
  }, [currentUser?.id]);

  const generateInvite = async (inviteType: 'registration' | 'link_existing') => {
    setGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-venture-invite', {
        body: { invite_type: inviteType },
      });

      if (error) {
        console.error('Error generating invite:', error);
        toast({
          title: 'Error',
          description: error.message || 'Failed to generate invite',
          variant: 'destructive',
        });
        return null;
      }

      toast({
        title: 'Success',
        description: 'Invite generated successfully',
      });

      await fetchInvites();
      return data;
    } catch (error) {
      console.error('Error generating invite:', error);
      toast({
        title: 'Error',
        description: 'Failed to generate invite',
        variant: 'destructive',
      });
      return null;
    } finally {
      setGenerating(false);
    }
  };

  const processInviteCode = async (inviteCode: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('process-venture-invite', {
        body: { invite_code: inviteCode },
      });

      if (error) {
        console.error('Error processing invite:', error);
        toast({
          title: 'Error',
          description: error.message || 'Invalid invite code',
          variant: 'destructive',
        });
        return false;
      }

      toast({
        title: 'Success',
        description: data.message || 'Successfully linked account',
      });

      return true;
    } catch (error) {
      console.error('Error processing invite:', error);
      toast({
        title: 'Error',
        description: 'Failed to process invite code',
        variant: 'destructive',
      });
      return false;
    }
  };

  return {
    invites,
    invitedUsers,
    loading,
    generating,
    generateInvite,
    processInviteCode,
    refetch: fetchInvites,
  };
};
